/**
 * Custom Thumbnail Handler
 * Manages thumbnail interactions and active states
 */

class CustomThumbnail {
  constructor(container) {
    this.container = container;
    this.thumbnails = container.querySelectorAll('.custom-thumbnail');
    this.currentIndex = 0;
    
    this.init();
  }

  init() {
    if (this.thumbnails.length === 0) return;

    // Set initial active state
    this.setActiveThumbnail(0);

    // Add click handlers
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.handleThumbnailClick(index);
      });

      thumbnail.addEventListener('pointerenter', () => {
        this.handleThumbnailHover(index);
      });
    });

    // Listen for slideshow changes from other sources (arrows, swipe, etc.)
    const mediaGallery = this.container.closest('media-gallery');
    if (mediaGallery) {
      // Listen for slide change events
      mediaGallery.addEventListener('slide:change', (e) => {
        if (e.detail && typeof e.detail.index === 'number') {
          this.setActiveThumbnail(e.detail.index);
        }
      });

      // Observe slideshow for changes
      this.observeSlideshow(mediaGallery);
    }
  }

  observeSlideshow(mediaGallery) {
    // Watch for active slide changes by monitoring aria-hidden
    const observer = new MutationObserver(() => {
      const slides = mediaGallery.querySelectorAll('slideshow-slide[ref="slides[]"]') ||
                     mediaGallery.querySelectorAll('[ref="slides[]"]') ||
                     mediaGallery.querySelectorAll('slideshow-slide');
      
      if (slides.length > 0) {
        // Find the slide with aria-hidden="false"
        slides.forEach((slide, index) => {
          if (slide.getAttribute('aria-hidden') === 'false' && index !== this.currentIndex) {
            this.setActiveThumbnail(index);
          }
        });
      }
    });

    const slideshow = mediaGallery.querySelector('slideshow-component') || 
                     mediaGallery.querySelector('slideshow-container') ||
                     mediaGallery.querySelector('[ref="slideshow"]') || 
                     mediaGallery;
    observer.observe(slideshow, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden']
    });
  }

  handleThumbnailClick(index) {
    this.setActiveThumbnail(index);
    this.scrollToMedia(index);
    
    // Dispatch custom event for external handlers
    this.container.dispatchEvent(
      new CustomEvent('thumbnail:change', {
        detail: { index, thumbnail: this.thumbnails[index] },
        bubbles: true
      })
    );
    
    // Also dispatch on media-gallery for slideshow to catch
    const mediaGallery = this.container.closest('media-gallery');
    if (mediaGallery) {
      mediaGallery.dispatchEvent(
        new CustomEvent('thumbnail:change', {
          detail: { index, thumbnail: this.thumbnails[index] },
          bubbles: true
        })
      );
    }
  }

  handleThumbnailHover(index) {
    // Optional: Add hover preview functionality
    this.container.dispatchEvent(
      new CustomEvent('thumbnail:hover', {
        detail: { index, thumbnail: this.thumbnails[index] }
      })
    );
  }

  setActiveThumbnail(index) {
    // Remove active state from all thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      thumb.classList.toggle('is-active', i === index);
    });

    this.currentIndex = index;
  }

  scrollToMedia(index) {
    // Find the slideshow component
    const mediaGallery = this.container.closest('media-gallery');
    if (!mediaGallery) return;

    // Try multiple methods to change the slide
    this.changeSlide(mediaGallery, index);
  }

  changeSlide(mediaGallery, index) {
    // Method 1: Find slideshow-slide elements with ref="slides[]"
    const slides = mediaGallery.querySelectorAll('slideshow-slide[ref="slides[]"]') ||
                   mediaGallery.querySelectorAll('[ref="slides[]"]') ||
                   mediaGallery.querySelectorAll('slideshow-slide');
    
    if (slides && slides.length > 0 && slides[index]) {
      // Update aria-hidden for all slides
      slides.forEach((slide, i) => {
        if (i === index) {
          // Show the selected slide
          slide.setAttribute('aria-hidden', 'false');
          slide.classList.add('is-active');
          // Remove any inline display styles that might hide it
          slide.style.display = '';
        } else {
          // Hide other slides
          slide.setAttribute('aria-hidden', 'true');
          slide.classList.remove('is-active');
        }
      });
      
      // Try to find slideshow component and trigger scroll
      const slideshowContainer = slides[index].closest('slideshow-container') ||
                                 slides[index].closest('[ref="slideshow"]') ||
                                 slides[index].closest('slideshow-component');
      
      if (slideshowContainer) {
        // Scroll to active slide smoothly
        slides[index].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'center' 
        });
      }
      
      // Try to trigger slideshow component method if available
      const slideshowComponent = mediaGallery.querySelector('slideshow-component');
      if (slideshowComponent) {
        // Try different method names
        if (slideshowComponent.goToSlide && typeof slideshowComponent.goToSlide === 'function') {
          slideshowComponent.goToSlide(index);
        } else if (slideshowComponent.setSlide && typeof slideshowComponent.setSlide === 'function') {
          slideshowComponent.setSlide(index);
        } else if (slideshowComponent.currentIndex !== undefined) {
          slideshowComponent.currentIndex = index;
        }
      }
      
      return;
    }

    // Method 2: Find slideshow custom element
    const slideshowElement = mediaGallery.querySelector('[ref="slideshow"]');
    
    if (slideshowElement) {
      // Try different API methods
      if (slideshowElement.goToSlide && typeof slideshowElement.goToSlide === 'function') {
        slideshowElement.goToSlide(index);
        return;
      }
      
      if (slideshowElement.setSlide && typeof slideshowElement.setSlide === 'function') {
        slideshowElement.setSlide(index);
        return;
      }
      
      if (slideshowElement.currentIndex !== undefined) {
        slideshowElement.currentIndex = index;
        return;
      }
    }

    // Method 3: Find grid media elements
    const gridMedia = mediaGallery.querySelectorAll('.media-gallery__grid [ref="media[]"]');
    if (gridMedia && gridMedia[index]) {
      gridMedia[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    // Method 4: Dispatch event for external handlers
    mediaGallery.dispatchEvent(new CustomEvent('slide:go-to', { 
      detail: { index },
      bubbles: true
    }));
  }

  // Public method to programmatically change thumbnail
  goToIndex(index) {
    if (index >= 0 && index < this.thumbnails.length) {
      this.handleThumbnailClick(index);
    }
  }

  // Public method to get current index
  getCurrentIndex() {
    return this.currentIndex;
  }
}

// Auto-initialize if container exists
document.addEventListener('DOMContentLoaded', () => {
  const thumbnailContainers = document.querySelectorAll('[data-custom-thumbnails]');
  thumbnailContainers.forEach(container => {
    new CustomThumbnail(container);
  });
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomThumbnail;
}

